/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ GOTRUE ANSWERS IN THE URL **FRAGMENT**, AND THAT IS THE WHOLE TRAP.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `hard-won.md` WP8c-1, restated because this is the file it is about: *"the verify-email landing
 * reads GoTrue's outcome from the URL fragment, and classifies four outcomes rather than three.
 * Both success and failure arrive in the fragment, so reading `location.search` finds nothing and
 * every visit looks unknown."*
 *
 * A fragment is never sent to a server either, which is why this is the client's job and why there
 * is no endpoint behind it.
 *
 * ⚠️ **Measured against a live stack rather than inferred**, `supabase start` on 26 Aug 2026:
 *
 * ```
 * success   #access_token=eyJ…&expires_at=…&expires_in=3600
 *            &refresh_token=53lhtumhbjp6&token_type=bearer&type=signup
 * dead link #error=access_denied&error_code=otp_expired
 *            &error_description=Email+link+is+invalid+or+has+expired
 * ```
 *
 * ⚠️⚠️ **AN EXPIRED LINK, AN ALREADY-USED LINK AND A GARBAGE TOKEN ARE THE SAME ANSWER.** All three
 * return `otp_expired`, verified by following one link twice and then following `token=deadbeef`.
 * So the screen must not claim "expired" — it cannot know that — and it must not offer "send a new
 * link", because the commonest cause is a link that was already used successfully, for which a
 * second link is the wrong remedy and there is no resend endpoint anyway.
 */

/**
 * What the landing page found.
 *
 * ⚠️ **`unknown` STAYS SEPARATE FROM `dead`, and WP8c-1 gives the reason**: offering a remedy for a
 * *truncated* URL sends somebody down a path that truncates the same way. No fragment at all means
 * the address was typed, or a mail client ate everything after the `#` — which is a different
 * conversation from a link that reached us and was refused.
 */
export type AuthOutcome =
  /** Confirmed. `kind` says which confirmation, because the two want different sentences. */
  | { readonly outcome: 'confirmed'; readonly kind: 'signup' | 'email_change' | 'other' }
  /** The link reached us and GoTrue refused it: expired, already used, or never valid. */
  | { readonly outcome: 'dead' }
  /** GoTrue reported something we do not have a sentence for. Carries the code for the page. */
  | { readonly outcome: 'error'; readonly code: string }
  /** No fragment, or nothing in it we recognise. */
  | { readonly outcome: 'unknown' };

/**
 * Read one.
 *
 * @param hash `location.hash`, with or without its leading `#`.
 *
 * ⚠️ **`URLSearchParams` on the fragment body, which is correct because GoTrue encodes it exactly
 * like a query string** — `error_description` arrives `+`-separated and percent-encoded, and
 * hand-splitting on `&` and `=` would leave `Email+link+is+invalid` on the page.
 */
export function readAuthFragment(hash: string): AuthOutcome {
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  if (body.trim() === '') return { outcome: 'unknown' };

  const params = new URLSearchParams(body);

  /**
   * ⚠️ **THE ERROR IS CHECKED FIRST, AND THE ORDER IS NOT ARBITRARY.** A refusal carries no
   * `access_token`, so either order works today — but if GoTrue ever answers with both, "there was
   * an error" is the safe reading and "you are confirmed" is the dangerous one. Ordering the checks
   * by which mistake is worse costs nothing.
   */
  const errorCode = params.get('error_code') ?? params.get('error');
  if (errorCode !== null && errorCode !== '') {
    /* Every dead-link cause collapses here, because GoTrue collapses them. */
    if (errorCode === 'otp_expired' || errorCode === 'access_denied') return { outcome: 'dead' };
    return { outcome: 'error', code: errorCode };
  }

  if (params.get('access_token') !== null) {
    const type = params.get('type');
    return {
      outcome: 'confirmed',
      /* ⚠️ `other` rather than a default of `signup`: a recovery or invite link landing here would
       * otherwise be announced as "your registration is complete", which is a sentence about
       * something that did not happen. */
      kind: type === 'signup' ? 'signup' : type === 'email_change' ? 'email_change' : 'other',
    };
  }

  return { outcome: 'unknown' };
}
